import { useState, useEffect, useRef } from 'react';
import { C, USERS } from '../styles/tokens.js';

const STORAGE_KEY = 'pium-collab:chat';
const ACTOR_KEY = 'pium-collab:chat-actor';
const USER_LIST = ['윤대표', '이팀장', '박대리', '김과장', '최주임'];

const SEED_MESSAGES = [
  { id: 1, by: '이팀장', text: '전북과학교육원 v3 업로드 했어요. 박대리님 예산표 확인 부탁드립니다.', ts: '09:12' },
  { id: 2, by: '박대리', text: '확인했습니다. 운반비 항목 추가하고 합계 다시 산출할게요.', ts: '09:25' },
  { id: 3, by: '김과장', text: '레퍼런스 이미지 p.21 국내 사례로 바꿔야 한다고 했죠? 후보 정리해서 공유드릴게요.', ts: '10:01' },
  { id: 4, by: '윤대표', text: '굿. 표지 폰트도 프리텐다드로 통일 부탁해요.', ts: '10:10' },
];

function loadMessages() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return SEED_MESSAGES;
}

function saveMessages(m) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(m)); } catch {} }

export default function ChatPanel({ open, onClose, currentProjectName }) {
  const [messages, setMessages] = useState(loadMessages);
  const [actor, setActor] = useState(() => {
    try { return localStorage.getItem(ACTOR_KEY) || '윤대표'; } catch { return '윤대표'; }
  });
  const [text, setText] = useState('');
  const [actorOpen, setActorOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { saveMessages(messages); }, [messages]);
  useEffect(() => { try { localStorage.setItem(ACTOR_KEY, actor); } catch {} }, [actor]);

  // 새 메시지 시 자동 스크롤
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = () => {
    if (!text.trim()) return;
    const now = new Date();
    const ts = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    setMessages(prev => [...prev, { id: Date.now(), by: actor, text: text.trim(), ts }]);
    setText('');
  };

  const clear = () => {
    if (window.confirm('대화 내역을 모두 삭제할까요?')) {
      setMessages([]);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 112, right: 0, bottom: 0, width: 340,
      background: C.surface, borderLeft: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column', zIndex: 60,
      boxShadow: '-8px 0 24px rgba(0,0,0,0.2)',
    }}>
      {/* 헤더 */}
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 18 }}>💬</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: C.text, fontWeight: 800 }}>팀 채팅</div>
          <div style={{ fontSize: 10, color: C.dim, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentProjectName || '전체'}</div>
        </div>
        <button onClick={clear} title="대화 비우기"
          style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 14, padding: '4px 6px' }}>🗑</button>
        <button onClick={onClose} title="닫기"
          style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>×</button>
      </div>

      {/* 메시지 영역 */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        {messages.length === 0 ? (
          <div style={{ fontSize: 12, color: C.dim, padding: '40px 20px', textAlign: 'center' }}>
            대화가 비어있습니다.<br/>첫 메시지를 보내보세요 👋
          </div>
        ) : messages.map((m, i) => {
          const isMe = m.by === actor;
          const prev = messages[i - 1];
          const showName = !prev || prev.by !== m.by;
          const col = USERS[m.by] || C.gold;
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', gap: 8, marginBottom: showName ? 12 : 4 }}>
              {showName ? (
                <div title={m.by} style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: `${col}33`, color: col,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, border: `1.5px solid ${col}66`,
                }}>{m.by[0]}</div>
              ) : (
                <div style={{ width: 28, flexShrink: 0 }} />
              )}
              <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                {showName && (
                  <div style={{ fontSize: 10, color: col, fontWeight: 700, marginBottom: 3 }}>{m.by}</div>
                )}
                <div style={{
                  padding: '8px 12px', borderRadius: 10,
                  background: isMe ? C.goldBg : C.surface2,
                  border: `1px solid ${isMe ? C.gold + '33' : C.border}`,
                  fontSize: 13, color: C.text, lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}>{m.text}</div>
                <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>{m.ts}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 입력 */}
      <div style={{ padding: 12, borderTop: `1px solid ${C.border}`, background: C.surface2 }}>
        {/* 사용자 전환 */}
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <button onClick={() => setActorOpen(o => !o)}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
              background: C.surface, color: C.text, fontSize: 11, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: `${USERS[actor]}33`, color: USERS[actor], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800 }}>{actor[0]}</div>
            <span style={{ flex: 1, textAlign: 'left' }}>발신자: {actor}</span>
            <span style={{ color: C.dim }}>▾</span>
          </button>
          {actorOpen && (
            <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, right: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: 4, boxShadow: '0 8px 20px rgba(0,0,0,0.4)', zIndex: 10 }}>
              {USER_LIST.map(u => (
                <div key={u} onClick={() => { setActor(u); setActorOpen(false); }}
                  style={{ padding: '6px 8px', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: actor === u ? C.goldBg : 'transparent' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: `${USERS[u]}33`, color: USERS[u], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800 }}>{u[0]}</div>
                  <span style={{ fontSize: 11, color: actor === u ? C.gold : C.text, fontWeight: 600 }}>{u}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={text} onChange={e => setText(e.target.value)}
            placeholder="메시지 입력... (Enter)"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            style={{ flex: 1, padding: '9px 12px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, outline: 'none' }} />
          <button onClick={send} disabled={!text.trim()}
            style={{ padding: '0 14px', borderRadius: 6, border: 'none',
              background: text.trim() ? C.gold : C.surface3,
              color: text.trim() ? '#000' : C.dim,
              fontSize: 12, cursor: text.trim() ? 'pointer' : 'not-allowed', fontWeight: 700 }}>전송</button>
        </div>
      </div>
    </div>
  );
}
